import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * The two-step confirmation for adding or removing a framework.
 *
 * ⚠ BOTH DIRECTIONS CONFIRM, AND FOR DIFFERENT REASONS.
 *
 *   · ADDING carries a LICENSING statement. Framework content reaches the tenant through
 *     VeriGRC's own arrangements, and the organisation's use of it — and of anything it adds
 *     alongside — is the organisation's responsibility. That belongs in front of the decision, not
 *     in a footer nobody reads after the fact.
 *   · REMOVING has to say what SURVIVES, because the obvious fear is that removing destroys work.
 *     The legacy path refused removal outright when evidence existed, which taught exactly that
 *     fear; stating the retention is how it gets untaught.
 *
 * ⓘ Neither dialog is a speed bump. Each says something the tenant could not otherwise know at the
 * moment they need it.
 */

export type ConfirmKind = 'add' | 'remove';

const COPY: Record<ConfirmKind, { title: string; body: string[]; confirm: string }> = {
  add: {
    title: 'Add this framework to your workspace?',
    body: [
      'This framework includes content made available through VeriGRC. Your organization is '
      + 'responsible for ensuring that its use of the framework and any organization-provided '
      + 'content complies with applicable licensing terms and rights.',
      'Adding the framework will make its requirements and applicable mappings available in your '
      + 'Compliance workspace. Existing work on VeriGRC Controls may immediately contribute to the '
      + "framework's calculated progress.",
    ],
    confirm: 'Add to Workspace',
  },
  remove: {
    title: 'Remove this framework from your workspace?',
    body: [
      'The framework will no longer appear as an active compliance program. Your existing control '
      + 'work, evidence, assessments, and history will be preserved. If you add the framework '
      + 'again later, that work will become available again.',
    ],
    confirm: 'Remove from Workspace',
  },
};

export function ConfirmFrameworkChange({
  kind, frameworkName, onCancel, onConfirm, busy,
}: {
  kind: ConfirmKind;
  frameworkName: string;
  onCancel: () => void;
  onConfirm: () => void;
  busy: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const copy = COPY[kind];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/60 p-[20px] pt-[14vh]"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        ref={ref} tabIndex={-1} role="alertdialog" aria-modal="true" aria-labelledby="confirm-fw-title"
        className="w-full max-w-[520px] rounded-[12px] border border-cert-neutral-border bg-bg-base p-[22px] shadow-2xl outline-none"
      >
        <h2 id="confirm-fw-title" className="text-[15.5px] font-semibold leading-[1.4] text-text-primary">
          {copy.title}
        </h2>
        <p className="mt-[6px] text-[12.5px] font-medium text-accent-cyan">{frameworkName}</p>

        <div className="mt-[14px] space-y-[10px]">
          {copy.body.map((p) => (
            <p key={p.slice(0, 24)} className="text-[12.5px] leading-[1.6] text-text-secondary">{p}</p>
          ))}
        </div>

        <div className="mt-[20px] flex items-center justify-end gap-[10px]">
          <button type="button" onClick={onCancel} disabled={busy}
            className="rounded-[7px] border border-cert-neutral-border px-[14px] py-[8px] text-[12.5px] text-text-secondary transition-colors hover:text-text-primary disabled:opacity-50">
            Cancel
          </button>
          <button
            type="button" onClick={onConfirm} disabled={busy}
            className={cn(
              'rounded-[7px] px-[14px] py-[8px] text-[12.5px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50',
              // ⚠ Removal is NOT styled as destructive, because it is not destructive — nothing is
              //   deleted. A red button would contradict the sentence directly above it.
              kind === 'add' ? 'bg-accent-cyan' : 'bg-slate-600',
            )}
          >
            {copy.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}
